import { useMemo, useState, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, TableSortLabel, Box, Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDateSort } from '../../../hooks/useDateSort';
import TableActionsMenu from '../../../components/common/TableActionsMenu';
import { AceEnterMezzomediaMetrics } from '../../../types/aceenter.types';
import type { Dayjs } from 'dayjs';

type Props = {
  data: AceEnterMezzomediaMetrics[];
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

export default function AceEnterMezzomediaTable({ data, selectedApp, startDate, endDate }: Props) {
  const rowsPerPage = 31;
  const [page, setPage] = useState(0);
  const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(data);
  
  // 테이블 ref - 엑셀 다운로드용
  const tableRef = useRef<HTMLTableElement>(null);

  // 파일명 생성
  const filename = useMemo(() => {
    const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
      return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
  }, [startDate, endDate, selectedApp]);

  const paged = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page]
  );

  const formatCurrency = (value: number) =>
    `₩${Math.round(value).toLocaleString('ko-KR')}`;

  const average = (vals: Array<number | undefined>) => {
    const nums = vals.filter((v): v is number => v !== undefined);
    return nums.length ? nums.reduce((s, v) => s + v, 0) / nums.length : 0;
  };

  // 합계 계산 (CTR만 평균, 나머지 합계)
  const totals = useMemo(() => {
    const totalReq = sortedData.reduce((sum, row) => sum + row.req, 0);
    const totalRes = sortedData.reduce((sum, row) => sum + row.res, 0);
    const totalImp = sortedData.reduce((sum, row) => sum + row.imp, 0);
    const totalClick = sortedData.reduce((sum, row) => sum + row.click, 0);
    const totalRevenue = sortedData.reduce((sum, row) => sum + row.revenue, 0);
    
    // CTR 평균 계산 (행별 CTR 산술평균)
    const avgCTR = average(
      sortedData.map((row) =>
        row.imp > 0 && row.click <= row.imp ? (row.click / row.imp) * 100 : undefined
      )
    );
    
    return {
      totalReq,
      totalRes,
      totalImp,
      totalClick,
      totalRevenue,
      avgCTR
    };
  }, [sortedData]);

  // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
  const allRowsDataForExcel = useMemo(() => {
    return sortedData.map((row) => {
      return [
        row.date,
        row.req.toLocaleString('ko-KR'),
        row.res.toLocaleString('ko-KR'),
        row.imp.toLocaleString('ko-KR'),
        row.click.toLocaleString('ko-KR'),
        `${((row.click / row.imp) * 100).toFixed(2)}%`,
        formatCurrency(row.revenue)
      ];
    });
  }, [sortedData]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TableActionsMenu 
          tableRef={tableRef} 
          allRowsData={allRowsDataForExcel}
          filename={filename}
          sheetName="메조미디어"
        />
      </Box>
      <TableContainer sx={{ backgroundColor: '#fff', borderRadius: 2, overflowX: 'auto' }}>
      <Table ref={tableRef}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active
                direction={sortDirection}
                onClick={toggleSort}
              >
                날짜
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>요청 수</TableCell>
            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>응답 수</TableCell>
            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>노출 수</TableCell>
            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>클릭 수</TableCell>
            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>CTR</TableCell>
            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>매출</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* 합계 행 */}
          <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
              {totals.totalReq.toLocaleString('ko-KR')}
            </TableCell>
            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
              {totals.totalRes.toLocaleString('ko-KR')}
            </TableCell>
            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
              {totals.totalImp.toLocaleString('ko-KR')}
            </TableCell>
            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
              {totals.totalClick.toLocaleString('ko-KR')}
            </TableCell>
            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {totals.avgCTR > 0 ? `${totals.avgCTR.toFixed(2)}%` : '-'}
                <Tooltip title="조회한 기간의 평균입니다" arrow>
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                </Tooltip>
              </Box>
            </TableCell>
            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
              {formatCurrency(totals.totalRevenue)}
            </TableCell>
          </TableRow>
          {paged.map((row, index) => (
            <TableRow 
              key={`${row.date}-${index}`}
            >
              <TableCell sx={{ fontWeight: 'medium' }}>
                {row.date}
              </TableCell>
              <TableCell sx={{ textAlign: 'right' }}>
                {row.req.toLocaleString('ko-KR')}
              </TableCell>
              <TableCell sx={{ textAlign: 'right' }}>
                {row.res.toLocaleString('ko-KR')}
              </TableCell>
              <TableCell sx={{ textAlign: 'right' }}>
                {row.imp.toLocaleString('ko-KR')}
              </TableCell>
              <TableCell sx={{ textAlign: 'right' }}>
                {row.click.toLocaleString('ko-KR')}
              </TableCell>
              <TableCell sx={{ textAlign: 'right' }}>
                {((row.click / row.imp) * 100).toFixed(2)}%
              </TableCell>
              <TableCell sx={{ textAlign: 'right' }}>
                {formatCurrency(row.revenue)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={sortedData.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </TableContainer>
    </Box>
  );
}
